/**
 * Icon — universal wrapper for Figma-exported SVGs in /public/icons/.
 *
 * Figma source: `icon` component-set (componentId 548:11337, 42 variants).
 * All SVGs exported with `fill="white"` as neutral baseline; actual rendered
 * colour is controlled per consumer via Tailwind `text-<token>` class —
 * this primitive wires that cascade via CSS `mask-image` + `background-color:
 * currentColor`. So `<Icon name="coin" className="text-coin-yellow" />`
 * renders a yellow coin; `<Icon name="forward" />` inside a `text-text-inverse`
 * parent renders a black arrow.
 *
 * Multi-color icons (brand logos like google.svg) are rendered via <img>
 * fallback to preserve their original palette — `MULTI_COLOR` allowlist.
 *
 * Props:
 *  - name   (string, required) — file basename in frontend/public/icons/
 *  - size   (number, px)       — both width + height, default 20
 *  - className? (string)       — passthrough (use `text-*` to control colour)
 *  - ...rest — forwarded to root element
 *
 * Rationale: `<img>` does NOT cascade CSS `color` to SVG contents. A white-
 * filled SVG stays white inside a black-text button — invisible. The mask
 * approach flips that: the SVG silhouette is the mask, the paint is the
 * current text colour of the parent.
 */

// Multi-color SVGs where we need to preserve original fills (brand assets).
const MULTI_COLOR = new Set(['google']);

export default function Icon({ name, size = 20, className = '', style, ...rest }) {
  if (MULTI_COLOR.has(name)) {
    return (
      <img
        src={`/icons/${name}.svg`}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        data-icon={name}
        className={className}
        style={style}
        {...rest}
      />
    );
  }
  const maskUrl = `url(/icons/${name}.svg)`;
  return (
    <span
      aria-hidden="true"
      data-icon={name}
      className={className}
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        backgroundColor: 'currentColor',
        WebkitMaskImage: maskUrl,
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskSize: 'contain',
        WebkitMaskPosition: 'center',
        maskImage: maskUrl,
        maskRepeat: 'no-repeat',
        maskSize: 'contain',
        maskPosition: 'center',
        flexShrink: 0,
        ...style,
      }}
      {...rest}
    />
  );
}
